<template>
    <div v-if="course">
        <div class="page-content">
            <template v-if="!isMicroLearningCourse">
                <div class="page-content__main">
                    <Banner :course="course" />
                    <CourseVideo
                        v-if="allDataLoaded && !isDigitalClassRoom && !isTestOnlyCourseType"
                        :key="videoRefreshIndex"
                        :isFree="course.free"
                        :isLoggedIn="isLoggedIn"
                        :courseId="course.id"
                        :autoPlay="autoPlay"
                    />
                    <DigitalClassRoomSignUp :course="course" v-if="isDigitalClassRoom" />
                    <NavigationButtons :collectionAllCourses="collectionAllCourses" :currentCourseId="course.id" v-if="isCollectionCoursePage"/>
                    <CourseOverview :course="course" :isLoggedIn="isLoggedIn" :canCourseTestBeOpen="canUserSeeTest" :courseWithoutTestCompleted="courseWithoutTestCompleted" :file="file"/>
                </div>
                <div class="page-content__aside">
                    <AuthorizationHours :durations="course.durations" />
                    <SingleCourseTableOfContents
                        v-if="allDataLoaded && videoLinks.length > 0"
                        :videoTimeLinks="videoLinks"
                        :course="course"
                        :canCourseTestBeOpen="canUserSeeTest" :courseWithoutTestCompleted="courseWithoutTestCompleted"
                    />
                    <CourseTest
                        :title = course.title
                        :courseId="course.id"
                        :canCourseTestBeOpen="canUserSeeTest"
                        :hasTest="course.isTestAvailable"
                        :courseWithoutTestCompleted="courseWithoutTestCompleted"
                        :isMicroLearningCourse="isMicroLearningCourse"
                        :isDemo="course.demo"
                        :isDigitalClassRoomTraining="isDigitalClassRoom"
                        :isUpcoming="isUpcoming(course)"
                        :isLiveWebinar="isLiveWebinar"
                        :isTestOnly="isTestOnlyCourseType"
                        :singleLearningPath="singleLearningPath"
                    />
                    <CollectionPreview
                        :collectionAllCourses="collectionAllCourses"
                        :currentCourseId="course.id"
                        :learningPathName="learningPathName"
                        v-if="isCollectionCoursePage"
                    />
                    <CollectionsCourseBelongs :collections="course.coursesCollections" :learningPaths="course.learningPathsCollection" />
                </div>
            </template>
            <template v-else>
                <template v-if="getMicroLearningSectionType === null">
                    <div class="page-content__main">
                        <Banner :course="course" />
                        <MicroLearningOverview
                            v-on:reInitializeVideo="reInitializeVideo"
                            :course="course"
                        />
                    </div>
                </template>
                <template v-if="getMicroLearningSectionType === 'video'">
                    <div class="page-content__main">
                        <Banner :course="course" />
                        <CourseVideo
                            :key="videoRefreshIndex"
                            :isFree="course.free"
                            :isLoggedIn="isLoggedIn"
                            :courseId="course.id"
                            v-on:reInitializeVideo="reInitializeVideo"
                        />
                        <MicroLearningNavigationButtons
                            v-if="!course.demo"
                            v-on:reInitializeVideo="reInitializeVideo"
                            :isCollectionCoursePage="isCollectionCoursePage"
                            :collectionAllCourses="collectionAllCourses"
                            :currentCourseId="course.id"
                        />
                        <CourseOverview :course="course" :isLoggedIn="isLoggedIn" :canCourseTestBeOpen="canUserSeeTest" :courseWithoutTestCompleted="courseWithoutTestCompleted" :file="file"/>
                    </div>
                </template>
                <template v-if="getMicroLearningSectionType === 'assignment'">
                    <div class="page-content__main">
                        <Banner :course="course" />
                        <MicroLearningMaterials
                            :isLoggedIn="isLoggedIn"
                            :isDemo="course.demo"
                            :materialTabDescription="$t('Microlearning.assignments_description')"
                        />
                        <CourseOverview :course="course" :isLoggedIn="isLoggedIn" :canCourseTestBeOpen="canUserSeeTest" :courseWithoutTestCompleted="courseWithoutTestCompleted" :file="file"/>
                        <MicroLearningNavigationButtons
                            v-if="!course.demo"
                            v-on:reInitializeVideo="reInitializeVideo"
                            :isCollectionCoursePage="isCollectionCoursePage"
                            :collectionAllCourses="collectionAllCourses"
                            :currentCourseId="course.id"
                        />
                    </div>
                </template>
                <template v-if="getMicroLearningSectionType === 'file'">
                    <div class="page-content__main">
                        <Banner :course="course" />
                        <MicroLearningMaterials
                            :isLoggedIn="isLoggedIn"
                            :isDemo="course.demo"
                            :materialTabDescription="$t('Microlearning.materials_description')"
                        />
                        <CourseOverview :course="course" :isLoggedIn="isLoggedIn" :canCourseTestBeOpen="canUserSeeTest" :courseWithoutTestCompleted="courseWithoutTestCompleted" :file="file"/>
                        <MicroLearningNavigationButtons
                            v-if="!course.demo"
                            v-on:reInitializeVideo="reInitializeVideo"
                            :isCollectionCoursePage="isCollectionCoursePage"
                            :collectionAllCourses="collectionAllCourses"
                            :currentCourseId="course.id"
                        />
                    </div>
                </template>
                <div class="page-content__aside">
                    <AuthorizationHours :durations="course.durations" />
                    <MicroLearningTableOfContents
                        v-if="microLearningSections"
                        :microLearningElements="microLearningSections"
                        :course="course"
                        :canCourseTestBeOpen="canUserSeeTest"
                        :courseWithoutTestCompleted="courseWithoutTestCompleted"
                        :currentMicroLearningSection="currentMicroLearningSection"
                        v-on:reInitializeVideo="reInitializeVideo"
                    />
                    <CourseTest
                        :title = course.title
                        :courseId="course.id"
                        :canCourseTestBeOpen="canUserSeeTest"
                        :hasTest="course.isTestAvailable"
                        :courseWithoutTestCompleted="courseWithoutTestCompleted"
                        :isMicroLearningCourse="isMicroLearningCourse"
                        :isDemo="course.demo"
                        :isDigitalClassRoomTraining="isDigitalClassRoom"
                        :isLiveWebinar="isLiveWebinar"
                        :isTestOnly="isTestOnlyCourseType"
                        :singleLearningPath="singleLearningPath"
                    />
                    <CollectionPreview
                        :collectionAllCourses="collectionAllCourses"
                        :currentCourseId="course.id"
                        :learningPathName="learningPathName"
                        v-if="isCollectionCoursePage"/>
                    <CollectionsCourseBelongs :collections="course.coursesCollections" :learningPaths="course.learningPathsCollection" />
                </div>
            </template>
        </div>
        <ModalAddToNewWatchlist/>
        <ModalShownAfterDemoWatched :isDemo="course.demo" />
        <ModalCourseVideoExceptionError />
    </div>
</template>

<script>
    import Banner from 'components/coursepage/banner/Banner'
    import CourseOverview from 'components/coursepage/courseoverview/CourseOverview'
    import CollectionPreview from 'components/coursepage/collectionpreview/CollectionPreview'
    import AuthorizationHours from 'components/coursepage/courseaddons/AuthorizationHours'
    import { mapGetters } from 'vuex'
    import CourseVideo from 'components/coursepage/coursevideo/CourseVideo'
    import ModalAddToNewWatchlist from 'components/shared/modals/ModalAddToNewWatchlist'
    import ModalShownAfterDemoWatched from 'components/shared/modals/ModalShownAfterDemoWatched'
    import CollectionsCourseBelongs from 'components/coursepage/courseaddons/CollectionsCourseBelongs'
    import moment from 'moment-timezone'
    import { checkIfCollectionCoursePage } from '@/router'
    import routeHelper from 'components/shared/mixins/routeHelper'
    import SingleCourseTableOfContents from 'components/coursepage/tableofcontents/SingleCourseTableOfContents'
    import MicroLearningTableOfContents from 'components/coursepage/microlearning/tableofcontents/MicroLearningTableOfContents'
    import MicroLearningMaterials from 'components/coursepage/microlearning/materialtabs/MicroLearningMaterials'
    import MicroLearningOverview from 'components/coursepage/microlearning/overview/MicroLearningOverview'
    import MicroLearningNavigationButtons from 'components/coursepage/microlearning/MicroLearningNavigationButtons'
    import CourseTest from 'components/coursepage/courseaddons/CourseTest'
    import microLearningHelper from 'components/shared/mixins/microLearningHelper'
    import NavigationButtons from 'components/coursepage/navigationbuttons/NavigationButtons'
    import DigitalClassRoomSignUp from 'components/coursepage/courseaddons/digitalclassrooms/DigitalClassRoomSignUp'
    import ModalCourseVideoExceptionError from 'components/shared/modals/ModalCourseVideoExceptionError'

    export default {
        name: 'CoursePage',
        components: {
            NavigationButtons,
            CourseVideo,
            CourseOverview,
            Banner,
            AuthorizationHours,
            CollectionPreview,
            ModalAddToNewWatchlist,
            ModalShownAfterDemoWatched,
            CollectionsCourseBelongs,
            SingleCourseTableOfContents,
            MicroLearningTableOfContents,
            MicroLearningMaterials,
            MicroLearningOverview,
            MicroLearningNavigationButtons,
            CourseTest,
            DigitalClassRoomSignUp,
            ModalCourseVideoExceptionError
        },
        mixins: [ routeHelper, microLearningHelper ],
        data () {
            return {
                videoDataReady: false,
                allDataLoaded: false,
                videoRefreshIndex: 0,
                autoPlay: false
            }
        },
        metaInfo () {
            return {
                title: this.courseTitle + ' - Eduhouse'
            }
        },
        computed: {
            ...mapGetters({
                course: 'Courses/getSingle',
                courseVideo: 'Courses/getCourseVideo',
                singleCollection: 'CoursesCollections/getSingleCollection',
                singleCollectionCourses: 'CoursesCollections/getSingleCollectionCourses',
                singleLearningPath: 'LearningPaths/getSingleLearningPath',
                singleLearningPathCourses: 'LearningPaths/getSingleLearningPathCourses',
                getCourseById: 'Courses/getCourseById',
                isLoggedIn: 'isLoggedIn',
                canUserSeeTest: 'VideoPlayer/canUserSeeTest',
                currentPackageSlug: 'CourseFilter/getCurrentPackageSlug',
                getCertificateForCourse: 'CompletedCourses/getCertificateForCourse',
                isDemo: 'VideoPlayer/showOnlyDemo',
                microLearningSections: 'MicroLearning/getMicroLearningSections',
                currentMicroLearningSection: 'MicroLearning/getMicroLearningSection',
                isMicroLearningCourse: 'MicroLearning/isMicroLearningCourse',
                isDigitalClassRoomTraining: 'Courses/isDigitalClassRoomTraining',
                microLearningVideoLogs: 'MicroLearning/getMicroLearningVideoLogs',
                lastWatchedSectionInfo: 'MicroLearning/getLastWatchedSectionInfo',
                currentUser: 'User/getCurrentUser',
                errorException: 'VideoLog/getErrorException',
                getNonLoggedException: 'VideoLog/getNonLoggedException'
            }),
            courseTitle () {
                return (this.course) ? this.course.title : ''
            },
            courseCompleted () {
                return this.getCertificateForCourse(this.course.id)
            },
            courseWithoutTestCompleted () {
                if (!this.course) {
                    return false
                }

                return !this.course.isTestAvailable && this.canUserSeeTest
            },
            collectionAllCourses () {
                let courses = []
                if (this.isCollection) {
                    courses = this.singleCollectionCourses
                } else if (this.isLearningPath) {
                    courses = this.singleLearningPathCourses
                }
                if (courses.length > 0) {
                    return courses
                        .map(course => this.getCourseById(course.id))
                        .filter(el => !!el)
                }

                return []
            },
            video () {
                if (this.courseVideo) {
                    return this.courseVideo
                }

                return null
            },
            videoLinks () {
                if (this.video) {
                    return this.video.elapsed || []
                }

                return []
            },
            videoId () {
                if (this.video) {
                    return this.video.elementId
                }

                return null
            },
            videoProps () {
                if (this.video) {
                    return this.video.url
                }

                return null
            },
            embedType () {
                if (this.videoProps) {
                    if (this.videoProps.indexOf('vimeo.com') >= 0) {
                        return 'VIMEO'
                    } else if (this.videoProps.indexOf('jwplayer') >= 0) {
                        return 'JW'
                    }
                }

                return 'UNKNOWN'
            },
            file () {
                if (this.course && this.course.files && this.course.files.length > 0) {
                    return this.course.files[0].url
                }

                return null
            },
            shouldLoadVideoLog () {
                return !!this.isLoggedIn && !!this.video && !!this.video.elementId
            },
            shouldLoadVideoLogForSingleCourse () {
                return this.shouldLoadVideoLog && !this.isMicroLearningCourse
            },
            shouldLoadVideoLogsForMicroLearning () {
                return this.shouldLoadVideoLog && this.isMicroLearningCourse && this.microLearningVideoLogs
            },
            isCollectionCoursePage () {
                return checkIfCollectionCoursePage(this.$route.name)
            },
            nextCourse () {
                let currentIndex = this.collectionAllCourses.findIndex(el => el.id === this.course.id)
                return this.collectionAllCourses[currentIndex + 1]
            },
            isMicroLearningCourse () {
                return this.course.elements.length > 1
            },
            isDigitalClassRoom () {
                return this.isDigitalClassRoomTraining(this.course)
            },
            isTestOnlyCourseType () {
                return this.course.type.value === 'test-only'
            },
            isLiveWebinar () {
                return this.course.type.value === 'webinar'
            },
            hasAccessToDigitalClassrooms () {
                return this.currentUser.hasAccessToDigitalClassrooms
            },
            isCollection () {
                return this.$route.params.collectionSlug
            },
            isLearningPath () {
                return this.$route.params.learningPathSlug
            },
            learningPathName () {
                if (this.isLearningPath) {
                    return this.singleLearningPath?.title
                }
                return ''
            }
        },
        methods: {
            async initializeVideo () {
                this.allDataLoaded = false
                let videoObject = {
                    courseId: this.course.id,
                    videoId: this.videoId,
                    embedType: this.embedType,
                    videoProps: this.videoProps,
                    showOnlyDemo: !!this.course.demo,
                    autoPlay: this.video && !!this.video.autoPlay,
                    isLive: this.isLiveWebinar
                }
                if (this.shouldLoadVideoLogForSingleCourse) {
                    this.currentVideoLog = await this.$store.dispatch('VideoLog/loadVideoLogForCourseAndVideo', {
                        courseId: this.course.id,
                        videoId: this.video.elementId
                    })
                    if (this.currentVideoLog) {
                        videoObject = {
                            ...videoObject,
                            position: this.currentVideoLog.position || 0,
                            videoDuration: this.currentVideoLog.durationSeconds || 99999999,
                            startingSequences: this.currentVideoLog.details.timeRanges || []
                        }
                    }
                }
                if (this.shouldLoadVideoLogsForMicroLearning) {
                    this.currentVideoLog = this.microLearningVideoLogs.find(videoLog => videoLog.elementId === this.video.elementId)
                    if (this.currentVideoLog) {
                        videoObject = {
                            ...videoObject,
                            position: this.currentVideoLog.position || 0,
                            videoDuration: this.currentVideoLog.durationSeconds || 99999999,
                            startingSequences: this.currentVideoLog.details.timeRanges || []
                        }
                    }
                }
                await this.$store.dispatch('VideoPlayer/clearVideoPlayer')
                await this.$store.dispatch('VideoPlayer/initiateVideoPlayer', videoObject)
                this.allDataLoaded = true
                this.videoRefreshIndex += 1
            },
            async refreshData () {
                try {
                    await this.$store.dispatch('Courses/loadSingleCourse', this.$route.params.courseSlug)
                    if (this.isDigitalClassRoom && !this.hasAccessToDigitalClassrooms) {
                        await this.$router.push({ name: 'page-no-permission' })
                    }

                    if (this.course.isMicrolearning) {
                        await this.setMicroLearningSectionsAndVideoLogs()
                    }
                    if (this.course.status === 'draft' || this.course.status === 'expired' || !this.isCourseExpirationDateInPast()) {
                        await this.$router.push({ name: 'page-content-expired' })
                    }
                } catch (e) {
                    if (e.code === 404 || e.code === 330 ||
                        e.response.status === 404 || e.response.status === 330) {
                        await this.$router.push({ name: 'page-not-found' })
                    }
                    if (e.code === 403 || e.code === 401 ||
                        e.response.status === 403 || e.response.status === 401) {
                        if (this.isLoggedIn) {
                            await this.$router.push({ name: 'page-no-permission' })
                        } else {
                            await this.$router.push({ name: 'login' })
                        }
                    }
                    if (e.code === 410 || e.response.status === 410) {
                        await this.$router.push({ name: 'page-not-found' })
                    }
                    throw e
                }
                if (this.isCollection) {
                    Promise.all(
                        [
                            this.$store.dispatch('CoursesCollections/loadSingleCollection', this.$route.params.collectionSlug),
                            this.$store.dispatch('Courses/loadCollectionCourses', this.$route.params.collectionSlug),
                        ]
                    )
                }
                if (this.isLearningPath) {
                    Promise.all(
                        [
                            this.$store.dispatch('LearningPaths/loadSingleLearningPath', this.$route.params.learningPathSlug),
                            this.$store.dispatch('Courses/loadLearningPathCoursesBySlug', this.$route.params.learningPathSlug)
                        ]
                    )
                }
                if (this.isLoggedIn) {
                    this.$store.dispatch('Notes/loadNotes')
                }
                this.initializeVideo()
            },
            isCourseExpirationDateInPast () {
                let today = moment().tz(this.getTimeZone()).startOf('day')
                let expiredDiff = this.course.expirationDate !== null ? today.diff(this.course.expirationDate, 'days') : null
                return expiredDiff <= 0
            },
            reInitializeVideo () {
                this.initializeVideo()
            }
        },
        async created () {
            const taskName = 'loadingCoursePage'
            try {
                await this.$store.dispatch('LoaderScreen/addTaskToLoader', { key: taskName, message: 'Loading CoursePage' })
                await this.refreshData()
            } catch (e) {}
            this.$store.dispatch('LoaderScreen/removeTaskFromLoader', taskName)
            if (window.HubSpotConversations && window.HubSpotConversations.widget) {
                window.HubSpotConversations.widget.load({ widgetOpen: true })
            }
            this.checkAndSetNewUrlSlug(this.course, 'course')
        },
        mounted () {
            this.$root.$on('open-course-test', (value, response) => {
                this.isCourseTestOpen = value
            })
        },
        async beforeDestroy () {
            if (this.currentPackageSlug !== this.$route.params.packageSlug || this.$route.name !== 'course-list') {
                this.$store.dispatch('CourseFilter/clearFilter')
            }
            this.allDataLoaded = false
            await this.$store.dispatch('Courses/clearSingleCourse')
            await this.$store.dispatch('VideoPlayer/clearVideoPlayer')
            await this.$store.dispatch('MicroLearning/clearMicroLearningSections')
        },
        watch: {
            async isLoggedIn (isLoggedIn) {
                if (isLoggedIn) {
                    await this.refreshData()
                }
            },
            errorException () {
                if (this.errorException) {
                    this.$modal.show('course-video-exception-error')
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .page-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin: 30px auto;

        [class*="desktop"] & {
            flex-direction: row;
        }

        &__main {
            [class*="desktop"] & {
                width: 1200px;
            }
        }

        &__aside {
            display: flex;
            flex-direction: column;

            [class*="desktop"] & {
                margin: 0 0 0 30px;
            }
        }

        &__toc,
        &__addons {
            display: flex;
            justify-content: center;
            flex-direction: column;
            margin: 0;
        }
    }
</style>
